import { useState, useEffect } from 'react'
import { Search, ChevronRight, Loader2, FolderOpen } from 'lucide-react'
import { useMatters } from '@/hooks/use-matters'
import { EmptyState } from '@/components/EmptyState'
import { UpgradePrompt } from '@/components/UpgradePrompt'
import type { Matter } from '@/types'

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])

  return debounced
}

interface MatterPickerScreenProps {
  onSelect: (matter: Matter) => void
}

export function MatterPickerScreen({ onSelect }: MatterPickerScreenProps) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const { data: matters, isLoading, error } = useMatters(debouncedSearch)

  const is402 = (error as { status?: number } | null)?.status === 402

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="p-3 border-b border-secondary-100 shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search matters…"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder:text-secondary-400"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {is402 ? (
          <UpgradePrompt />
        ) : isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 size={20} className="animate-spin text-primary-400" />
          </div>
        ) : error ? (
          <EmptyState
            title="Could not load matters"
            description={(error as { message?: string }).message ?? 'Network error'}
          />
        ) : !matters?.length ? (
          <EmptyState
            icon={<FolderOpen size={28} />}
            title={debouncedSearch ? 'No matters found' : 'No open matters'}
            description={debouncedSearch ? 'Try a different search term.' : 'Create a matter in Athenty to get started.'}
          />
        ) : (
          <ul className="divide-y divide-secondary-100">
            {matters.map((matter) => (
              <MatterRow key={matter.id} matter={matter} onSelect={onSelect} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function MatterRow({ matter, onSelect }: { matter: Matter; onSelect: (m: Matter) => void }) {
  return (
    <li>
      <button
        onClick={() => onSelect(matter)}
        className="w-full flex items-center justify-between px-4 py-3 gap-3 text-left hover:bg-secondary-50 transition-colors"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-secondary-900 truncate">{matter.name}</p>
          {matter.reference && (
            <p className="text-xs text-secondary-400 truncate">{matter.reference}</p>
          )}
        </div>
        <ChevronRight size={14} className="text-secondary-300 shrink-0" />
      </button>
    </li>
  )
}
